import AccountCircle from '@mui/icons-material/AccountCircle';
import LinkIcon from '@mui/icons-material/Link';
import MenuIcon from '@mui/icons-material/Menu';
import AppBar from '@mui/material/AppBar';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import {alpha, useTheme} from '@mui/material/styles';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import {useEffect, useState, type MouseEvent, type ReactElement, type ReactNode} from 'react';
import {useLocation, useNavigate} from 'react-router';
import {getEvent} from '../api/eventApi';
import {getMenuLinks} from '../api/menuLinkApi';
import NotificationDialog from '../components/NotificationDialog';
import PageBottomSpacing from '../components/PageBottomSpacing';
import RemainingDaysDialog from '../components/RemainingDaysDialog';
import type {Event} from '../entities/Event';
import type {MenuLink} from '../entities/MenuLink';
import {AGB_ROUTE, IMPRINT_ROUTE, PRIVACY_POLICY_ROUTE, PROFILE_ROUTE, REGISTRATION_ROUTE} from '../routing/routes';
import {login, logout, session} from '../security/keycloak';
import {themeConfig, themeUrl} from '../theme/themeConfig';
import {isNotFound} from '../util/httpErrors';
import AdventCountdown from './AdventCountdown';

export interface DrawerEntry {
  icon: ReactElement;
  name: string;
  link: string;
}

interface Props {
  drawerEntries: DrawerEntry[];
  children?: ReactNode;
}

const DRAWER_WIDTH = 272;

const LEGAL_ENTRIES = [
  {name: 'AGB', link: AGB_ROUTE},
  {name: 'Datenschutzerklärung', link: PRIVACY_POLICY_ROUTE},
  {name: 'Impressum', link: IMPRINT_ROUTE},
];

/** App bar, navigation drawer and the page background shared by all pages. */
export default function PageWithNavigation({drawerEntries, children}: Props) {
  const theme = useTheme();
  const config = themeConfig();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = !useMediaQuery(theme.breakpoints.up('sm'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement>();
  const [menuLinks, setMenuLinks] = useState<MenuLink[]>([]);
  const [event, setEvent] = useState<Event>();
  const [remainingDaysDialogIsClosed, setRemainingDaysDialogIsClosed] = useState(false);

  useEffect(() => {
    getMenuLinks().then(setMenuLinks);
    getEvent()
      .then(setEvent)
      .catch((error: unknown) => {
        if (!isNotFound(error)) {
          throw error;
        }
      });
  }, []);

  const closeMenu = () => setAnchorEl(undefined);
  const goTo = (link: string) => {
    setMobileOpen(false);
    closeMenu();
    void navigate(link);
  };
  const internalEntry = (name: string, link: string, icon?: ReactNode) => (
    <ListItemButton
      key={link}
      component="a"
      href={link}
      selected={location.pathname === link}
      onClick={(event: MouseEvent) => {
        event.preventDefault();
        goTo(link);
      }}
    >
      {icon && <ListItemIcon>{icon}</ListItemIcon>}
      <ListItemText primary={name}/>
    </ListItemButton>
  );

  const drawerContent = (
    <Box sx={{display: 'flex', flexDirection: 'column', minHeight: '100%'}}>
      <Toolbar/>
      <List sx={{paddingTop: 2}}>
        {drawerEntries.map(entry => internalEntry(entry.name, entry.link, entry.icon))}
      </List>
      {menuLinks.length > 0 && (
        <List
          dense={true}
          subheader={
            <ListSubheader disableSticky={true} sx={{backgroundColor: 'transparent', lineHeight: '32px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', fontSize: '0.7rem', paddingLeft: 3}}>
              Links
            </ListSubheader>
          }
        >
          {menuLinks.map(entry => (
            <ListItemButton key={entry.id} component="a" href={entry.url} target="_blank" rel="noopener" sx={{minHeight: 38}}>
              <ListItemIcon><LinkIcon fontSize="small"/></ListItemIcon>
              <ListItemText primary={entry.name}/>
            </ListItemButton>
          ))}
        </List>
      )}
      <Box sx={{flexGrow: 1}}/>
      {event && <AdventCountdown event={event}/>}
      <Divider sx={{marginX: 2}}/>
      <List dense={true} sx={{paddingBottom: 2}}>
        {LEGAL_ENTRIES.map(entry => (
          <ListItemButton
            key={entry.link}
            component="a"
            href={entry.link}
            selected={location.pathname === entry.link}
            sx={{minHeight: 34}}
            onClick={(clickEvent: MouseEvent) => {
              clickEvent.preventDefault();
              goTo(entry.link);
            }}
          >
            <ListItemText primary={entry.name} slotProps={{primary: {sx: {fontSize: '0.8rem', fontWeight: 500, color: 'text.secondary'}}}}/>
          </ListItemButton>
        ))}
      </List>
      <PageBottomSpacing/>
    </Box>
  );
  const drawerSx = {
    zIndex: theme.zIndex.drawer,
    [theme.breakpoints.up('sm')]: {width: DRAWER_WIDTH, flexShrink: 0},
  };
  const paperSx = {width: DRAWER_WIDTH, zIndex: theme.zIndex.drawer};

  return (
    <Box sx={{minHeight: '100%', display: 'flex'}}>
      <RemainingDaysDialog event={event} onDialogClosed={() => setRemainingDaysDialogIsClosed(true)}/>
      {remainingDaysDialogIsClosed && <NotificationDialog/>}

      <AppBar position="fixed" sx={{zIndex: theme.zIndex.drawer + 1}}>
        <Toolbar sx={{gap: 1.5}}>
          <IconButton
            color="inherit"
            edge="start"
            aria-label="Menü öffnen"
            onClick={() => setMobileOpen(previous => !previous)}
            sx={{[theme.breakpoints.up('sm')]: {display: 'none'}}}
          >
            <MenuIcon/>
          </IconButton>
          <Box
            component="img"
            src={themeUrl('android-icon-96x96.png')}
            alt=""
            sx={{width: 36, height: 36, borderRadius: '10px', boxShadow: `0 2px 8px ${alpha('#000', 0.25)}`, display: {xs: 'none', sm: 'block'}}}
          />
          <Typography
            variant="h6"
            component="div"
            color="inherit"
            noWrap={true}
            sx={{flexGrow: 1, fontWeight: 800, letterSpacing: '-0.01em', fontSize: {xs: '1.05rem', sm: '1.2rem'}}}
          >
            {config.appBarTitle}
          </Typography>
          <IconButton
            aria-label="Benutzer"
            color="inherit"
            onClick={clickEvent => setAnchorEl(clickEvent.currentTarget)}
            sx={{padding: 0.5, backgroundColor: alpha('#ffffff', 0.14), '&:hover': {backgroundColor: alpha('#ffffff', 0.24)}, borderRadius: 999}}
          >
            {session.currentUser ? (
              <Avatar sx={{width: 32, height: 32, backgroundColor: '#ffffff', color: 'primary.main', fontWeight: 800, fontSize: '0.9rem'}}>
                {initials(session.currentUser.email)}
              </Avatar>
            ) : (
              <AccountCircle sx={{fontSize: 32}}/>
            )}
          </IconButton>
          <Menu
            id="menu-appbar"
            anchorEl={anchorEl}
            open={anchorEl !== undefined}
            onClose={closeMenu}
            transformOrigin={{vertical: 'top', horizontal: 'right'}}
            anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
          >
            {!session.currentUser && [
              <MenuItem key="login" onClick={() => login()}>Anmelden</MenuItem>,
              <MenuItem key="register" onClick={() => goTo(REGISTRATION_ROUTE)}>Registrieren</MenuItem>,
            ]}
            {session.currentUser && [
              <MenuItem key="email" disabled={true} sx={{fontSize: '0.85rem'}}>{session.currentUser.email}</MenuItem>,
              <MenuItem key="profile" onClick={() => goTo(PROFILE_ROUTE)}>Profil bearbeiten</MenuItem>,
              <MenuItem key="logout" onClick={() => logout()}>Abmelden</MenuItem>,
            ]}
          </Menu>
        </Toolbar>
      </AppBar>

      <nav>
        {isMobile ? (
          <SwipeableDrawer
            swipeAreaWidth={40}
            open={mobileOpen}
            sx={drawerSx}
            slotProps={{paper: {sx: paperSx}}}
            onClose={() => setMobileOpen(false)}
            onOpen={() => setMobileOpen(true)}
          >
            {drawerContent}
          </SwipeableDrawer>
        ) : (
          <Drawer variant="permanent" open={true} sx={drawerSx} slotProps={{paper: {sx: paperSx}}}>
            {drawerContent}
          </Drawer>
        )}
      </nav>

      <Box component="main" sx={{flexGrow: 1, minWidth: 0}}>
        <Toolbar/>
        {children}
      </Box>

      <Box
        component="img"
        src={themeUrl('bg.png')}
        alt=""
        sx={{
          position: 'fixed',
          bottom: 0,
          right: 0,
          zIndex: theme.zIndex.drawer + 1,
          pointerEvents: 'none',
          width: '100%',
          [theme.breakpoints.down('lg')]: {width: '130%'},
          [theme.breakpoints.down('sm')]: {zIndex: theme.zIndex.drawer - 1, width: '200%'},
        }}
      />
    </Box>
  );
}

function initials(email: string | undefined): string {
  return (email ?? '?').slice(0, 2).toUpperCase();
}
